// Central HTTP client for the Due Health v1 API.
//
// A single axios instance with a request interceptor that attaches the JWT
// bearer token, and a response interceptor that clears the session on 401. All
// data access in the app goes through this client (see src/api/hooks.js).

import axios from 'axios';
import { API_BASE_URL } from './config';

const TOKEN_KEY = 'dh_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) =>
  token ? localStorage.setItem(TOKEN_KEY, token) : localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({ baseURL: API_BASE_URL });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired/invalid — drop it so the app falls back to the login flow.
      setToken(null);
    }
    return Promise.reject(normalizeError(error));
  }
);

// Surface a clean message from the API's {error, detail} envelope or validation body.
export function normalizeError(error) {
  const data = error.response?.data;
  let message = error.message;
  if (typeof data?.detail === 'string') message = data.detail;
  else if (Array.isArray(data?.detail)) message = data.detail[0]?.msg || message;
  error.userMessage = message;
  return error;
}

// Thin helpers used by the query hooks.
export const get = (url, params) => api.get(url, { params }).then((r) => r.data);
export const post = (url, body) => api.post(url, body).then((r) => r.data);
export const patch = (url, body) => api.patch(url, body).then((r) => r.data);
export const put = (url, body) => api.put(url, body).then((r) => r.data);
